import { useState, useEffect, useMemo } from 'react'
import { useSubscribeToEvent } from 'services/event-bus/useEventBus'
import { EVENT_TYPES } from 'services/event-bus/eventTypes'
import { generationProgressService } from 'services/generationProgress'

interface IProgressData {
  progress: number | null
  message: string | null
  assetId?: string
}

export const useGenerationProgress = ({
  mediaItemId,
  assetId,
  isGenerating
}: {
  mediaItemId?: string | null
  assetId?: string | number
  isGenerating?: boolean
}) => {
  const [progressData, setProgressData] = useState<IProgressData>({
    progress: null,
    message: null,
    assetId: undefined
  })

  const stringifiedAssetId = assetId ? String(assetId) : undefined

  const mediaId = useMemo(() => {
    if (mediaItemId) return mediaItemId
    if (stringifiedAssetId) {
      const generatorId =
        generationProgressService.getGeneratorIdPerAssetId(stringifiedAssetId)
      if (generatorId) return generatorId
      return stringifiedAssetId
    }
    return null
  }, [mediaItemId, stringifiedAssetId])

  const resetData = () => {
    setProgressData({ progress: null, message: null, assetId: undefined })
  }
  // Get initial progress from service ( as maybe it was reloaded and kept in local storage )
  useEffect(() => {
    if (!mediaId) {
      resetData()
      return
    }

    // Check if generator is active
    const generatorIsActive = generationProgressService.isActive(mediaId)

    if (!generatorIsActive && isGenerating) {
      generationProgressService.resume(mediaId)
    }

    const initialProgress = generationProgressService.getProgress(mediaId)
    const initialMessage = generationProgressService.getMessage(mediaId)

    if (initialProgress !== null || initialMessage !== null) {
      setProgressData({
        progress: initialProgress,
        message: initialMessage,
        assetId: stringifiedAssetId
      })
    }
  }, [mediaId, isGenerating, stringifiedAssetId])

  // Subscribe to progress updates
  useSubscribeToEvent(
    EVENT_TYPES.GENERATION_PROGRESS_UPDATE,
    (
      updatedMediaItemId: string,
      updatedAssetId: string,
      updatedProgress: number,
      updatedMessage: string,
      updatedActiveState: boolean
    ) => {
      // Resolve mediaId dynamically to handle cases where localStorage is updated after mount
      if (!mediaId) return

      if (updatedMediaItemId === mediaId) {
        // Only update progress/message if generator is active
        if (updatedActiveState) {
          setProgressData({
            progress: updatedProgress,
            message: updatedMessage,
            assetId: updatedAssetId ? String(updatedAssetId) : undefined
          })
        } else {
          // Generator stopped - clear progress
          setProgressData({ progress: null, message: null })
        }
      }
    }
  )

  return progressData
}
